import React from 'react';
import * as THREE from 'three';
import type { NPCModelProps, NPCModelRegistry } from '../types';

// ==================== LORD OF THE RINGS CREATURES ====================

// Orc - hunched warrior
function OrcModel({ hovered }: NPCModelProps) {
  return (
    <group>
      {/* Corps voûté */}
      <mesh position={[0, 0.35, 0]} rotation={[0.2, 0, 0]}>
        <capsuleGeometry args={[0.12, 0.25, 6, 8]} />
        <meshStandardMaterial color="#4a5a4a" roughness={0.9} />
      </mesh>
      {/* Tête */}
      <mesh position={[0, 0.55, 0.08]}>
        <sphereGeometry args={[0.1, 8, 8]} />
        <meshStandardMaterial color="#5a6a5a" roughness={0.9} />
      </mesh>
      {/* Oreilles pointues */}
      <mesh position={[0.1, 0.58, 0.05]} rotation={[0, 0, 0.5]}>
        <coneGeometry args={[0.03, 0.08, 4]} />
        <meshStandardMaterial color="#5a6a5a" roughness={0.9} />
      </mesh>
      <mesh position={[-0.1, 0.58, 0.05]} rotation={[0, 0, -0.5]}>
        <coneGeometry args={[0.03, 0.08, 4]} />
        <meshStandardMaterial color="#5a6a5a" roughness={0.9} />
      </mesh>
      {/* Yeux rouges */}
      <mesh position={[0.04, 0.57, 0.15]}>
        <sphereGeometry args={[0.02, 6, 6]} />
        <meshStandardMaterial color="#ff3300" emissive="#ff0000" emissiveIntensity={0.5} />
      </mesh>
      <mesh position={[-0.04, 0.57, 0.15]}>
        <sphereGeometry args={[0.02, 6, 6]} />
        <meshStandardMaterial color="#ff3300" emissive="#ff0000" emissiveIntensity={0.5} />
      </mesh>
      {/* Armure rudimentaire */}
      <mesh position={[0, 0.38, 0.05]}>
        <boxGeometry args={[0.2, 0.15, 0.12]} />
        <meshStandardMaterial color="#2a2a2a" metalness={0.6} roughness={0.5} />
      </mesh>
      {/* Bras avec arme */}
      <mesh position={[0.15, 0.35, 0.05]} rotation={[0.5, 0, 0.3]}>
        <capsuleGeometry args={[0.04, 0.15, 4, 6]} />
        <meshStandardMaterial color="#4a5a4a" roughness={0.9} />
      </mesh>
      <mesh position={[-0.15, 0.35, 0.05]} rotation={[0.5, 0, -0.3]}>
        <capsuleGeometry args={[0.04, 0.15, 4, 6]} />
        <meshStandardMaterial color="#4a5a4a" roughness={0.9} />
      </mesh>
      {/* Cimeterre */}
      <mesh position={[0.22, 0.3, 0.15]} rotation={[0.8, 0.3, 0.5]}>
        <boxGeometry args={[0.02, 0.25, 0.04]} />
        <meshStandardMaterial color="#3a3a3a" metalness={0.8} roughness={0.3} />
      </mesh>
      {/* Jambes */}
      <mesh position={[0.06, 0.12, 0]}>
        <capsuleGeometry args={[0.04, 0.15, 4, 6]} />
        <meshStandardMaterial color="#4a5a4a" roughness={0.9} />
      </mesh>
      <mesh position={[-0.06, 0.12, 0]}>
        <capsuleGeometry args={[0.04, 0.15, 4, 6]} />
        <meshStandardMaterial color="#4a5a4a" roughness={0.9} />
      </mesh>
    </group>
  );
}

// Nazgul - spectral wraith
function NazgulModel({ hovered }: NPCModelProps) {
  return (
    <group>
      {/* Robe noire flottante */}
      <mesh position={[0, 0.4, 0]}>
        <coneGeometry args={[0.25, 0.7, 8]} />
        <meshStandardMaterial color="#0a0a0a" roughness={1} />
      </mesh>
      {/* Capuche */}
      <mesh position={[0, 0.75, 0.05]}>
        <sphereGeometry args={[0.12, 8, 8]} />
        <meshStandardMaterial color="#0a0a0a" roughness={1} />
      </mesh>
      {/* Vide sous la capuche (pas de visage) */}
      <mesh position={[0, 0.73, 0.1]}>
        <sphereGeometry args={[0.08, 8, 8]} />
        <meshBasicMaterial color="#000000" />
      </mesh>
      {/* Yeux spectraux rouges */}
      <mesh position={[0.03, 0.74, 0.12]}>
        <sphereGeometry args={[0.015, 6, 6]} />
        <meshBasicMaterial color="#ff0000" />
      </mesh>
      <mesh position={[-0.03, 0.74, 0.12]}>
        <sphereGeometry args={[0.015, 6, 6]} />
        <meshBasicMaterial color="#ff0000" />
      </mesh>
      {/* Bras avec épée */}
      <mesh position={[0.18, 0.5, 0.1]} rotation={[0.3, 0, 0.5]}>
        <capsuleGeometry args={[0.03, 0.2, 4, 6]} />
        <meshStandardMaterial color="#0a0a0a" roughness={1} />
      </mesh>
      {/* Épée Morgul */}
      <mesh position={[0.25, 0.35, 0.2]} rotation={[0.5, 0, 0.3]}>
        <boxGeometry args={[0.015, 0.4, 0.03]} />
        <meshStandardMaterial color="#2a3a4a" metalness={0.9} roughness={0.2} emissive="#001133" emissiveIntensity={0.3} />
      </mesh>
      {/* Aura sombre */}
      <mesh position={[0, 0.4, 0]}>
        <sphereGeometry args={[0.4, 8, 8]} />
        <meshBasicMaterial color="#000000" transparent opacity={0.2} />
      </mesh>
    </group>
  );
}

// Ent - walking tree shepherd
function EntModel({ hovered }: NPCModelProps) {
  return (
    <group>
      {/* Tronc principal */}
      <mesh position={[0, 0.6, 0]}>
        <cylinderGeometry args={[0.15, 0.2, 1, 8]} />
        <meshStandardMaterial color="#4a3a2a" roughness={1} />
      </mesh>
      {/* Texture écorce */}
      <mesh position={[0, 0.6, 0.12]}>
        <boxGeometry args={[0.08, 0.6, 0.05]} />
        <meshStandardMaterial color="#3a2a1a" roughness={1} />
      </mesh>
      {/* Tête/Couronne */}
      <mesh position={[0, 1.15, 0]}>
        <sphereGeometry args={[0.18, 8, 8]} />
        <meshStandardMaterial color="#4a3a2a" roughness={1} />
      </mesh>
      {/* Yeux sages */}
      <mesh position={[0.08, 1.15, 0.14]}>
        <sphereGeometry args={[0.03, 6, 6]} />
        <meshStandardMaterial color="#ffcc00" emissive="#886600" emissiveIntensity={0.3} />
      </mesh>
      <mesh position={[-0.08, 1.15, 0.14]}>
        <sphereGeometry args={[0.03, 6, 6]} />
        <meshStandardMaterial color="#ffcc00" emissive="#886600" emissiveIntensity={0.3} />
      </mesh>
      {/* Feuillage */}
      <mesh position={[0, 1.35, 0]}>
        <sphereGeometry args={[0.25, 8, 8]} />
        <meshStandardMaterial color="#2d5a2d" roughness={0.9} />
      </mesh>
      <mesh position={[0.15, 1.25, 0.1]}>
        <sphereGeometry args={[0.12, 6, 6]} />
        <meshStandardMaterial color="#3a6a3a" roughness={0.9} />
      </mesh>
      <mesh position={[-0.15, 1.28, -0.05]}>
        <sphereGeometry args={[0.1, 6, 6]} />
        <meshStandardMaterial color="#2d5a2d" roughness={0.9} />
      </mesh>
      {/* Bras branches */}
      <mesh position={[0.25, 0.9, 0]} rotation={[0, 0, -0.8]}>
        <cylinderGeometry args={[0.04, 0.06, 0.4, 6]} />
        <meshStandardMaterial color="#4a3a2a" roughness={1} />
      </mesh>
      <mesh position={[-0.25, 0.85, 0]} rotation={[0, 0, 0.7]}>
        <cylinderGeometry args={[0.04, 0.06, 0.35, 6]} />
        <meshStandardMaterial color="#4a3a2a" roughness={1} />
      </mesh>
      {/* Jambes racines */}
      <mesh position={[0.1, 0.1, 0.05]}>
        <cylinderGeometry args={[0.06, 0.08, 0.25, 6]} />
        <meshStandardMaterial color="#3a2a1a" roughness={1} />
      </mesh>
      <mesh position={[-0.1, 0.1, -0.05]}>
        <cylinderGeometry args={[0.06, 0.08, 0.25, 6]} />
        <meshStandardMaterial color="#3a2a1a" roughness={1} />
      </mesh>
    </group>
  );
}

// Balrog - demon of fire and shadow
function BalrogModel({ hovered }: NPCModelProps) {
  return (
    <group>
      {/* Corps massif de feu et ombre */}
      <mesh position={[0, 0.6, 0]}>
        <capsuleGeometry args={[0.3, 0.5, 8, 10]} />
        <meshStandardMaterial color="#1a0a0a" roughness={0.8} emissive="#ff2200" emissiveIntensity={0.3} />
      </mesh>
      {/* Tête cornue */}
      <mesh position={[0, 1, 0.1]}>
        <sphereGeometry args={[0.18, 8, 8]} />
        <meshStandardMaterial color="#1a0a0a" roughness={0.8} emissive="#ff3300" emissiveIntensity={0.2} />
      </mesh>
      {/* Cornes */}
      <mesh position={[0.12, 1.15, 0]} rotation={[0.3, 0, 0.3]}>
        <coneGeometry args={[0.04, 0.2, 6]} />
        <meshStandardMaterial color="#0a0a0a" />
      </mesh>
      <mesh position={[-0.12, 1.15, 0]} rotation={[0.3, 0, -0.3]}>
        <coneGeometry args={[0.04, 0.2, 6]} />
        <meshStandardMaterial color="#0a0a0a" />
      </mesh>
      {/* Yeux de feu */}
      <mesh position={[0.08, 1.02, 0.2]}>
        <sphereGeometry args={[0.04, 6, 6]} />
        <meshBasicMaterial color="#ff6600" />
      </mesh>
      <mesh position={[-0.08, 1.02, 0.2]}>
        <sphereGeometry args={[0.04, 6, 6]} />
        <meshBasicMaterial color="#ff6600" />
      </mesh>
      {/* Ailes de feu/ombre */}
      <mesh position={[0.4, 0.8, -0.1]} rotation={[0.2, 0.5, 0.3]} scale={[0.6, 0.8, 0.05]}>
        <boxGeometry args={[1, 1, 1]} />
        <meshStandardMaterial color="#1a0505" transparent opacity={0.7} emissive="#ff2200" emissiveIntensity={0.2} />
      </mesh>
      <mesh position={[-0.4, 0.8, -0.1]} rotation={[0.2, -0.5, -0.3]} scale={[0.6, 0.8, 0.05]}>
        <boxGeometry args={[1, 1, 1]} />
        <meshStandardMaterial color="#1a0505" transparent opacity={0.7} emissive="#ff2200" emissiveIntensity={0.2} />
      </mesh>
      {/* Fouet de feu */}
      <mesh position={[0.35, 0.5, 0.2]} rotation={[0.5, 0.5, 0]}>
        <cylinderGeometry args={[0.015, 0.01, 0.8, 6]} />
        <meshStandardMaterial color="#ff4400" emissive="#ff2200" emissiveIntensity={0.8} />
      </mesh>
      {/* Épée enflammée */}
      <mesh position={[-0.3, 0.6, 0.15]} rotation={[0.3, -0.3, -0.5]}>
        <boxGeometry args={[0.04, 0.5, 0.08]} />
        <meshStandardMaterial color="#ff3300" emissive="#ff0000" emissiveIntensity={0.6} />
      </mesh>
      {/* Flammes autour */}
      <pointLight position={[0, 0.6, 0]} color="#ff4400" intensity={2} distance={3} decay={2} />
      {/* Jambes */}
      <mesh position={[0.15, 0.2, 0]}>
        <capsuleGeometry args={[0.1, 0.3, 6, 8]} />
        <meshStandardMaterial color="#1a0a0a" roughness={0.8} />
      </mesh>
      <mesh position={[-0.15, 0.2, 0]}>
        <capsuleGeometry args={[0.1, 0.3, 6, 8]} />
        <meshStandardMaterial color="#1a0a0a" roughness={0.8} />
      </mesh>
    </group>
  );
}

// Hobbit - small friendly folk
function HobbitModel({ hovered }: NPCModelProps) {
  return (
    <group>
      {/* Corps petit et rond */}
      <mesh position={[0, 0.2, 0]}>
        <capsuleGeometry args={[0.08, 0.12, 6, 8]} />
        <meshStandardMaterial color="#8b7355" roughness={0.8} />
      </mesh>
      {/* Tête ronde */}
      <mesh position={[0, 0.38, 0]}>
        <sphereGeometry args={[0.08, 8, 8]} />
        <meshStandardMaterial color="#e8c8a8" roughness={0.7} />
      </mesh>
      {/* Cheveux bouclés */}
      <mesh position={[0, 0.42, -0.02]}>
        <sphereGeometry args={[0.07, 8, 8]} />
        <meshStandardMaterial color="#5a3a1a" roughness={1} />
      </mesh>
      {/* Yeux */}
      <mesh position={[0.03, 0.39, 0.06]}>
        <sphereGeometry args={[0.015, 6, 6]} />
        <meshStandardMaterial color="#4a6a8a" />
      </mesh>
      <mesh position={[-0.03, 0.39, 0.06]}>
        <sphereGeometry args={[0.015, 6, 6]} />
        <meshStandardMaterial color="#4a6a8a" />
      </mesh>
      {/* Gilet */}
      <mesh position={[0, 0.22, 0.02]}>
        <boxGeometry args={[0.12, 0.1, 0.06]} />
        <meshStandardMaterial color="#4a6a4a" roughness={0.9} />
      </mesh>
      {/* Bras */}
      <mesh position={[0.1, 0.22, 0]} rotation={[0, 0, 0.5]}>
        <capsuleGeometry args={[0.025, 0.08, 4, 6]} />
        <meshStandardMaterial color="#e8c8a8" roughness={0.7} />
      </mesh>
      <mesh position={[-0.1, 0.22, 0]} rotation={[0, 0, -0.5]}>
        <capsuleGeometry args={[0.025, 0.08, 4, 6]} />
        <meshStandardMaterial color="#e8c8a8" roughness={0.7} />
      </mesh>
      {/* Jambes courtes */}
      <mesh position={[0.04, 0.07, 0]}>
        <capsuleGeometry args={[0.03, 0.08, 4, 6]} />
        <meshStandardMaterial color="#6a5a4a" roughness={0.8} />
      </mesh>
      <mesh position={[-0.04, 0.07, 0]}>
        <capsuleGeometry args={[0.03, 0.08, 4, 6]} />
        <meshStandardMaterial color="#6a5a4a" roughness={0.8} />
      </mesh>
      {/* Grands pieds poilus */}
      <mesh position={[0.04, 0.02, 0.03]}>
        <sphereGeometry args={[0.04, 6, 6]} />
        <meshStandardMaterial color="#5a4a3a" roughness={1} />
      </mesh>
      <mesh position={[-0.04, 0.02, 0.03]}>
        <sphereGeometry args={[0.04, 6, 6]} />
        <meshStandardMaterial color="#5a4a3a" roughness={1} />
      </mesh>
    </group>
  );
}

// Gandalf - the grey wizard
function GandalfModel({ hovered }: NPCModelProps) {
  return (
    <group>
      {/* Robe longue */}
      <mesh position={[0, 0.35, 0]}>
        <coneGeometry args={[0.15, 0.6, 8]} />
        <meshStandardMaterial color="#808080" roughness={0.9} />
      </mesh>
      {/* Torse */}
      <mesh position={[0, 0.6, 0]}>
        <capsuleGeometry args={[0.08, 0.15, 6, 8]} />
        <meshStandardMaterial color="#808080" roughness={0.9} />
      </mesh>
      {/* Tête */}
      <mesh position={[0, 0.78, 0]}>
        <sphereGeometry args={[0.08, 8, 8]} />
        <meshStandardMaterial color="#e8d8c8" roughness={0.7} />
      </mesh>
      {/* Barbe */}
      <mesh position={[0, 0.68, 0.05]}>
        <coneGeometry args={[0.06, 0.2, 6]} />
        <meshStandardMaterial color="#d0d0d0" roughness={1} />
      </mesh>
      {/* Chapeau pointu */}
      <mesh position={[0, 0.92, 0]}>
        <coneGeometry args={[0.1, 0.2, 8]} />
        <meshStandardMaterial color="#505050" roughness={0.9} />
      </mesh>
      {/* Yeux sages */}
      <mesh position={[0.03, 0.8, 0.06]}>
        <sphereGeometry args={[0.015, 6, 6]} />
        <meshStandardMaterial color="#6090c0" />
      </mesh>
      <mesh position={[-0.03, 0.8, 0.06]}>
        <sphereGeometry args={[0.015, 6, 6]} />
        <meshStandardMaterial color="#6090c0" />
      </mesh>
      {/* Bâton */}
      <mesh position={[0.15, 0.4, 0]} rotation={[0.1, 0, 0.1]}>
        <cylinderGeometry args={[0.015, 0.02, 0.8, 6]} />
        <meshStandardMaterial color="#5a4a3a" roughness={0.9} />
      </mesh>
      {/* Cristal au sommet du bâton */}
      <mesh position={[0.18, 0.82, 0.02]}>
        <octahedronGeometry args={[0.03]} />
        <meshStandardMaterial color="#ffffff" emissive="#aaccff" emissiveIntensity={0.5} transparent opacity={0.8} />
      </mesh>
    </group>
  );
}

// Registry export
export const lordOfTheRingsNPCs: NPCModelRegistry = {
  orc: OrcModel,
  nazgul: NazgulModel,
  ent: EntModel,
  balrog: BalrogModel,
  hobbit: HobbitModel,
  gandalf: GandalfModel,
};
